/**
 * Alta o edicion del precio propio de una comuna (ADM-10).
 *
 * Se abre siempre desde el modal de una region, asi que la region viene dada
 * y solo se elige la comuna, entre las de esa region. Al editar viene todo y
 * solo se corrige costo o plazo (la comuna identifica la fila en el servidor:
 * guardar la misma comuna reemplaza su precio).
 */
import { useState } from 'react';
import type { FormEvent } from 'react';

import type { TarifaRegionAdmin } from '@elcarpintero/compartido';

import { guardarTarifaComuna } from '../../servicios/despacho-admin.servicio';
import { pesos } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { Modal } from './Modal';

/** Lo que el modal trae ya completo al abrirse. */
export interface ComunaEnEdicion {
  codigoRegion: string;
  comuna?: string;
  costo?: number;
  plazo?: string | null;
}

interface ModalTarifaComunaProps {
  /** `null` con el modal cerrado. */
  edicion: ComunaEnEdicion | null;
  regiones: TarifaRegionAdmin[];
  alGuardar: () => void;
  onCerrar: () => void;
}

export function ModalTarifaComuna({ edicion, onCerrar, ...resto }: ModalTarifaComunaProps) {
  const editando = edicion?.comuna !== undefined;
  const region = resto.regiones.find((candidata) => candidata.codigo === edicion?.codigoRegion);

  return (
    <Modal
      titulo={
        editando ? `Precio propio de ${edicion?.comuna ?? ''}` : 'Precio propio para una comuna'
      }
      bajada={`Región ${region?.nombre ?? ''}: para una comuna que no cuesta lo mismo que el resto.`}
      abierto={edicion !== null}
      onCerrar={onCerrar}
    >
      {edicion !== null ? (
        <FormularioComuna
          key={`${edicion.codigoRegion}-${edicion.comuna ?? ''}`}
          edicion={edicion}
          onCerrar={onCerrar}
          {...resto}
        />
      ) : null}
    </Modal>
  );
}

interface FormularioComunaProps extends Omit<ModalTarifaComunaProps, 'edicion'> {
  edicion: ComunaEnEdicion;
}

function FormularioComuna({ edicion, regiones, alGuardar, onCerrar }: FormularioComunaProps) {
  const editando = edicion.comuna !== undefined;
  const { codigoRegion } = edicion;
  const [comuna, setComuna] = useState(edicion.comuna ?? '');
  const [costo, setCosto] = useState(edicion.costo === undefined ? '' : String(edicion.costo));
  const [plazo, setPlazo] = useState(edicion.plazo ?? '');
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  const region = regiones.find((candidata) => candidata.codigo === codigoRegion);
  const diferencia = region !== undefined && costo !== '' ? Number(costo) - region.costo : null;

  async function guardar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setEnviando(true);
    setFalla(null);

    try {
      await guardarTarifaComuna({
        comuna,
        codigoRegion,
        costo: Number(costo),
        plazo: plazo.trim() === '' ? null : plazo.trim(),
      });
      alGuardar();
      onCerrar();
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
      setEnviando(false);
    }
  }

  return (
    <form
      onSubmit={(evento) => {
        void guardar(evento);
      }}
    >
      <div className="campo">
        <label htmlFor="exc-comuna">Comuna</label>
        <select
          id="exc-comuna"
          className="entrada"
          value={comuna}
          disabled={region === undefined || editando}
          onChange={(evento) => {
            setComuna(evento.target.value);
          }}
        >
          <option value="">Elige una comuna</option>
          {(region?.comunas ?? []).map((nombre) => (
            <option key={nombre} value={nombre}>
              {nombre}
            </option>
          ))}
        </select>
        {!editando ? (
          <p className="campo-ayuda">Si ya tiene precio propio, se reemplaza por este.</p>
        ) : null}
      </div>

      <div className="campo">
        <label htmlFor="exc-costo">Costo del despacho</label>
        <div className="entrada-pesos">
          <span aria-hidden="true">$</span>
          <input
            id="exc-costo"
            className="entrada"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={costo}
            onChange={(evento) => {
              setCosto(evento.target.value);
            }}
          />
        </div>
        {region !== undefined ? (
          <p className="campo-ayuda">
            {region.nombre} cobra {pesos(region.costo)}
            {diferencia !== null && diferencia !== 0
              ? `; esta comuna, ${pesos(Math.abs(diferencia))} ${diferencia > 0 ? 'más' : 'menos'}.`
              : '.'}
          </p>
        ) : null}
      </div>

      <div className="campo">
        <label htmlFor="exc-plazo">Plazo (opcional)</label>
        <input
          id="exc-plazo"
          className="entrada"
          type="text"
          maxLength={80}
          value={plazo}
          placeholder={region?.plazo ?? ''}
          onChange={(evento) => {
            setPlazo(evento.target.value);
          }}
        />
        <p className="campo-ayuda">En blanco se usa el plazo de la región.</p>
      </div>

      {falla !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          {falla}
        </p>
      ) : null}

      <div className="modal-acciones">
        <button type="button" className="btn btn-contorno" onClick={onCerrar}>
          Cancelar
        </button>
        <button
          type="submit"
          className="btn btn-primario"
          disabled={enviando || comuna === '' || costo === ''}
        >
          {enviando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}
